import { format, parseISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./BookingModal.module.css";
import { CalendarView } from "./CalendarView";
import { TimeSlotSelector } from "./TimeSlotSelector";

interface Provider {
	id: string;
	name: string;
	specialty: string;
	image?: string;
}

interface BookingModalProps {
	provider: Provider;
	onClose: () => void;
	onBook: () => void;
	userId: string;
	apiBaseUrl: string;
	authToken: string;
}

interface TimeSlot {
	startTime: string;
	endTime: string;
	duration: number;
	availabilityId: string;
	timezone?: string;
}

export function BookingModal({
	provider,
	onClose,
	onBook,
	userId,
	apiBaseUrl,
	authToken,
}: BookingModalProps) {
	const { t } = useClientTranslations("consultExpert");
	const { logCommunication, logShared } = useAnalytics();
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [selectedTime, setSelectedTime] = useState<string | null>(null);
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
		null,
	);
	const [notes, setNotes] = useState("");
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
	const [loadingSlots, setLoadingSlots] = useState(false);
	const [availableDates, setAvailableDates] = useState<Date[]>([]);
	const [isBooking, setIsBooking] = useState(false);
	const [availabilityData, setAvailabilityData] = useState<{
		[key: string]: TimeSlot[];
	}>({});
	const [_loadingAvailability, setLoadingAvailability] = useState(true);
	const [providerTimezone, setProviderTimezone] = useState<string>("UTC");
	const notesId = useId();

	// Log modal opened event
	useEffect(() => {
		logCommunication("booking_modal_opened", {
			provider_id: provider.id,
			provider_name: provider.name,
		});
	}, [logCommunication, provider.id, provider.name]);

	const handleClose = () => {
		logCommunication("booking_modal_closed", {
			provider_id: provider.id,
			had_date_selected: !!selectedDate,
			had_time_selected: !!selectedTime,
		});
		onClose();
	};

	const loadAvailableDates = useCallback(async () => {
		try {
			setLoadingAvailability(true);

			// Get current month range
			const currentDate = new Date();
			const startOfMonth = new Date(
				currentDate.getFullYear(),
				currentDate.getMonth(),
				1,
			);
			const endOfMonth = new Date(
				currentDate.getFullYear(),
				currentDate.getMonth() + 1,
				0,
			);

			const startDateString = format(startOfMonth, "yyyy-MM-dd");
			const endDateString = format(endOfMonth, "yyyy-MM-dd");

			// Single API call for the entire month
			const response = await fetch(
				`${apiBaseUrl}/api/scheduling/bulk-availability?providerId=${provider.id}&startDate=${startDateString}&endDate=${endDateString}`,
				{
					headers: {
						Authorization: `Bearer ${authToken}`,
					},
				},
			);

			if (response.ok) {
				const data = await response.json();
				const dates: Date[] = [];
				const availabilityMap: { [key: string]: TimeSlot[] } = {};

				// Set provider timezone from response
				if (data.providerTimezone) {
					setProviderTimezone(data.providerTimezone);
				}

				data.availability.forEach(
					(dayData: { date: string; slots: TimeSlot[] }) => {
						if (dayData.slots && dayData.slots.length > 0) {
							dates.push(parseISO(dayData.date));
							availabilityMap[dayData.date] = dayData.slots;
						}
					},
				);

				setAvailableDates(dates);
				setAvailabilityData(availabilityMap);
			}
		} catch (error) {
			console.error("Error loading available dates:", error);
			setAvailableDates([]);
			setAvailabilityData({});
		} finally {
			setLoadingAvailability(false);
		}
	}, [provider.id, apiBaseUrl, authToken]);

	const loadAvailableSlots = useCallback(async () => {
		if (!selectedDate) return;

		try {
			setLoadingSlots(true);
			const dateString = format(selectedDate, "yyyy-MM-dd");

			// Use cached availability data if available
			if (availabilityData[dateString]) {
				setAvailableTimeSlots(availabilityData[dateString]);
			} else {
				// Fallback to individual API call if not in cache
				const response = await fetch(
					`${apiBaseUrl}/api/scheduling/available-slots?providerId=${provider.id}&date=${dateString}`,
					{
						headers: {
							Authorization: `Bearer ${authToken}`,
						},
					},
				);

				if (response.ok) {
					const data = await response.json();
					setAvailableTimeSlots(data.slots || []);
				} else {
					console.warn("Failed to load slots:", response.status);
					setAvailableTimeSlots([]);
				}
			}
		} catch (error) {
			console.error("Error loading slots:", error);
			setAvailableTimeSlots([]);
		} finally {
			setLoadingSlots(false);
		}
	}, [selectedDate, provider.id, availabilityData, apiBaseUrl, authToken]);

	// Load available dates when modal opens
	useEffect(() => {
		if (provider.id) {
			loadAvailableDates();
		}
	}, [provider.id, loadAvailableDates]);

	// Fetch available slots when date is selected
	useEffect(() => {
		if (selectedDate && provider.id) {
			loadAvailableSlots();
		} else {
			setAvailableTimeSlots([]);
			setSelectedTime(null);
		}
	}, [selectedDate, provider.id, loadAvailableSlots]);

	const handleConfirmBooking = async () => {
		if (!selectedDate || !selectedTime || isBooking) return;

		try {
			setIsBooking(true);

			// Calculate start and end times in provider's timezone
			const [hours, minutes] = selectedTime.split(":").map(Number);

			// Create date in provider's timezone
			const dateInProviderTz = format(selectedDate, "yyyy-MM-dd");
			const timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
			const dateTimeString = `${dateInProviderTz}T${timeString}`;

			// Convert to UTC for database storage
			const startDateTime = fromZonedTime(dateTimeString, providerTimezone);
			const endDateTime = new Date(
				startDateTime.getTime() + (selectedTimeSlot?.duration || 30) * 60000,
			);

			const response = await fetch(
				`${apiBaseUrl}/api/scheduling/scheduled-meetings`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${authToken}`,
					},
					body: JSON.stringify({
						providerUserId: provider.id,
						patientUserId: userId,
						availabilityId: selectedTimeSlot?.availabilityId || null,
						scheduledDate: format(selectedDate, "yyyy-MM-dd"),
						startTime: startDateTime.toISOString(),
						endTime: endDateTime.toISOString(),
						duration: selectedTimeSlot?.duration || 30,
						title: `${t("booking.consultationWith")} ${provider.name}`,
						notes,
						meetLink: null,
						timezone: providerTimezone,
					}),
				},
			);

			if (response.ok) {
				toast.success(
					t("booking.successToast", {
						providerName: provider.name,
						date: format(selectedDate, "MMMM d, yyyy"),
						time: selectedTime,
					}),
				);
				logCommunication("booking_confirmed", {
					provider_id: provider.id,
					provider_name: provider.name,
					scheduled_date: format(selectedDate, "yyyy-MM-dd"),
					scheduled_time: selectedTime,
					duration: selectedTimeSlot?.duration || 30,
					has_notes: notes.length > 0,
				});
				// Cross-surface scheduling KPI (also fired by JourneyBookingModal).
				// Neutral name via logShared + `source` per the shared-event
				// convention so booked-meeting funnels span every entry point.
				logShared("meeting_booked", undefined, {
					source: "consult_expert",
				});
				onBook();
			} else {
				const errorData = await response.json();
				console.error("Failed to create booking:", errorData);
				toast.error(t("booking.errorToast"));
				logCommunication("booking_error", {
					provider_id: provider.id,
					error_type: "api_error",
					status_code: response.status,
				});
			}
		} catch (error) {
			console.error("Error creating booking:", error);
			toast.error(t("booking.errorToast"));
			logCommunication("booking_error", {
				provider_id: provider.id,
				error_type: "exception",
				error_message: error instanceof Error ? error.message : "Unknown error",
			});
		} finally {
			setIsBooking(false);
		}
	};

	return (
		<div className={styles.overlay} role="presentation">
			<div
				className={styles.modal}
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
			>
				{/* Header */}
				<div className={styles.header}>
					<div>
						<h2 className={styles.title}>{t("booking.title")}</h2>
						<p className={styles.subtitle}>
							{t("booking.subtitle")} {provider.name}
						</p>
					</div>
					<button
						type="button"
						onClick={handleClose}
						className={styles.closeButton}
					>
						✕
					</button>
				</div>

				{/* Content */}
				<div className={styles.content}>
					{!showConfirmation ? (
						<div className={styles.gridLayout}>
							{/* Calendar Section */}
							<div>
								<h3 className={styles.sectionTitle}>
									<span className={styles.icon}>📅</span>
									{t("booking.selectDate")}
								</h3>
								<CalendarView
									selectedDate={selectedDate}
									onSelectDate={(date) => {
										setSelectedDate(date);
										if (date) {
											logCommunication("booking_date_selected", {
												provider_id: provider.id,
												selected_date: format(date, "yyyy-MM-dd"),
											});
										}
									}}
									availableDates={availableDates}
								/>
							</div>

							{/* Time Slot Section */}
							<div>
								<h3 className={styles.sectionTitle}>
									<span className={styles.icon}>🕐</span>
									{t("booking.selectTime")}{" "}
									{providerTimezone !== "UTC" && `(${providerTimezone})`}
								</h3>
								{selectedDate ? (
									loadingSlots ? (
										<div className={styles.loading}>
											<div className={styles.spinner} />
											<p className={styles.loadingText}>
												{t("booking.loadingTimes")}
											</p>
										</div>
									) : availableTimeSlots.length > 0 ? (
										<TimeSlotSelector
											availableSlots={availableTimeSlots.map(
												(slot) => slot.startTime,
											)}
											selectedSlot={selectedTime}
											onSelectSlot={(time) => {
												setSelectedTime(time);
												const slot = availableTimeSlots.find(
													(s) => s.startTime === time,
												);
												setSelectedTimeSlot(slot || null);
												logCommunication("booking_time_selected", {
													provider_id: provider.id,
													selected_time: time,
													duration: slot?.duration || 30,
												});
											}}
										/>
									) : (
										<div className={styles.emptySlots}>
											{t("booking.noSlots")}
										</div>
									)
								) : (
									<div className={styles.emptySlots}>
										{t("booking.selectDateFirst")}
									</div>
								)}

								{/* Duration Display */}
								{selectedTime && selectedTimeSlot && (
									<div className={styles.durationSection}>
										<div className={styles.durationLabel}>
											{t("booking.sessionDuration")}
										</div>
										<div className={styles.durationValue}>
											{selectedTimeSlot.duration} {t("booking.minutes")}
										</div>
									</div>
								)}

								{/* Notes */}
								{selectedTime && (
									<div className={styles.notesSection}>
										<label htmlFor={notesId} className={styles.notesLabel}>
											{t("booking.notesLabel")}
										</label>
										<textarea
											id={notesId}
											value={notes}
											onChange={(e) => setNotes(e.target.value)}
											onBlur={() => {
												if (notes.trim()) {
													logCommunication("booking_notes_added", {
														provider_id: provider.id,
														notes_length: notes.length,
													});
												}
											}}
											rows={3}
											placeholder={t("booking.notesPlaceholder")}
											className={styles.notesTextarea}
										/>
									</div>
								)}
							</div>
						</div>
					) : (
						/* Confirmation Screen */
						<div className={styles.confirmation}>
							<div className={styles.confirmIcon}>📅</div>
							<h3 className={styles.confirmTitle}>
								{t("booking.confirmTitle")}
							</h3>
							<div className={styles.confirmDetails}>
								<p>
									<strong>{t("booking.provider")}</strong> {provider.name}
								</p>
								<p>
									<strong>{t("booking.date")}</strong>{" "}
									{selectedDate && format(selectedDate, "MMMM d, yyyy")}
								</p>
								<p>
									<strong>{t("booking.time")}</strong> {selectedTime} (
									{selectedTimeSlot?.duration || 30} {t("booking.minutes")})
									{providerTimezone !== "UTC" && ` - ${providerTimezone}`}
								</p>
								<p className={styles.meetLink}>
									<span className={styles.icon}>📹</span>
									{t("booking.meetLinkInfo")}
								</p>
							</div>
							<div className={styles.warning}>
								<span className={styles.warningIcon}>⚠️</span>
								<p className={styles.warningText}>
									{t("booking.calendarInviteWarning", {
										providerName: provider.name,
									})}
								</p>
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className={styles.footer}>
					{!showConfirmation ? (
						<>
							<button
								type="button"
								onClick={handleClose}
								className={styles.cancelButton}
							>
								{t("booking.cancel")}
							</button>
							<button
								type="button"
								onClick={() => {
									setShowConfirmation(true);
									logCommunication("booking_review", {
										provider_id: provider.id,
										selected_date: selectedDate
											? format(selectedDate, "yyyy-MM-dd")
											: null,
										selected_time: selectedTime,
										duration: selectedTimeSlot?.duration || 30,
									});
								}}
								disabled={!selectedDate || !selectedTime}
								className={styles.continueButton}
							>
								{t("booking.continue")}
							</button>
						</>
					) : (
						<>
							<button
								type="button"
								onClick={() => {
									logCommunication("booking_back_clicked", {
										provider_id: provider.id,
										selected_date: selectedDate
											? format(selectedDate, "yyyy-MM-dd")
											: null,
										selected_time: selectedTime,
									});
									setShowConfirmation(false);
								}}
								className={styles.backButton}
							>
								{t("booking.back")}
							</button>
							<button
								type="button"
								onClick={handleConfirmBooking}
								disabled={isBooking}
								className={styles.confirmButton}
							>
								{isBooking ? t("booking.booking") : t("booking.confirmBooking")}
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
